import React from 'react';
import MasterPanel from './MasterPanel';
import GenDocRow from './GenDocRow';
import { MasterCollapse, MasterChip, getS3, mutS3, OkFile, UpBtn, RefFile, S3Row, BulkSaveBtn, renameUpload, dispName } from './stepcommon';
import { collectGenFiles, downloadCollectFile, downloadUploadFile, fetchCollectBlob } from './docgen';
import { mval } from './collect';
import { s3upload, fileUrl } from './api';
import type { Project } from './types';

interface Props {
  p: Project;
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
  masterOpen: boolean;
  onToggleMaster: () => void;
  onPv: (k: string, idx?: number) => void;
  onVer: (k: string) => void;
  onFilePv: (name: string, title?: string) => void;
}

export default function Step3({ p, onPatch, toast, masterOpen, onToggleMaster, onPv, onVer, onFilePv }: Props) {
  const s3 = getS3(p);
  const lkCert = p.collect ? mval(p.collect.gp, 'eduCert') : '';
  const m = p.master;
  const setVics = (f: 'id' | 'pw', v: string) => onPatch(mutS3(p, (st) => { st.vics[f] = v; }));
  const upCert = async (f0: File) => {
    const f = renameUpload(f0, 'GP 교육 수료증', p.org || '');
    toast('업로드 중 · ' + f.name);
    try {
      const r = await s3upload(p.id, f);
      onPatch(mutS3(p, (st) => { st.up.cert = r.name; st.upKey = st.upKey || {}; st.upKey.cert = r.key; }));
      toast('업로드됨 · ' + r.name);
    } catch (e) {
      toast('업로드 실패 · ' + ((e as Error).message || ''));
    }
  };
  const upReg = async (f0: File) => {
    const f = renameUpload(f0, '조합 주소지 등기부등본', p.org || '');
    toast('업로드 중 · ' + f.name);
    try {
      const r = await s3upload(p.id, f);
      onPatch(mutS3(p, (st) => { st.up.reg = r.name; st.upKey = st.upKey || {}; st.upKey.reg = r.key; }));
      toast('업로드됨 · ' + r.name);
    } catch (e) {
      toast('업로드 실패 · ' + ((e as Error).message || ''));
    }
  };
  const openKey = (key: string | undefined, fname: string) => {
    if (!key) { toast('미리보기할 파일이 없습니다 · 다시 업로드해 주세요'); return; }
    fileUrl(key, fname, true).then((u) => window.open(u, '_blank', 'noopener')).catch((e) => toast((e as Error).message));
  };
  const certKey = s3.upKey ? s3.upKey.cert : undefined;
  const regKey = s3.upKey ? s3.upKey.reg : undefined;
  const certDisp = s3.up.cert ? dispName(s3.up.cert, 'GP 교육 수료증', p.org || '') : s3.up.cert;
  const regDisp = s3.up.reg ? dispName(s3.up.reg, '조합 주소지 등기부등본', p.org || '') : s3.up.reg;
  const bulkFiles = [
    ...(certKey && s3.up.cert ? [{ key: certKey, name: s3.up.cert }] : []),
    // 수료증 직접 업로드 전이면 1단계 연동본 포함 (7단계 누락건과 동일 유형 보완)
    ...(!s3.up.cert && lkCert ? [{ key: '', name: lkCert, getBlob: () => fetchCollectBlob(p, lkCert) }] : []),
    ...(regKey && s3.up.reg ? [{ key: regKey, name: s3.up.reg }] : []),
    ...collectGenFiles(p, ['rule', 'plan', 'expert', 'privacy']),
  ];
  const gd = (k: string, seal: boolean) => <GenDocRow p={p} k={k} seal={seal} onPatch={onPatch} onVer={onVer} onPv={onPv} toast={toast} />;

  return (
    <div className="sact">
      <div className="sact-h" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>📄 조합 결성계획 신청</span>
        <BulkSaveBtn files={bulkFiles} toast={toast} pid={p.id} />
      </div>
      <MasterCollapse open={masterOpen} onToggle={onToggleMaster} chip={<MasterChip m={p.master} />} title="📋 공통정보 (마스터)" sub="열기를 누르면 펼쳐집니다 · 여기 값으로 4개 문서 자동 생성">
        <MasterPanel p={p} onPatch={onPatch} toast={toast} withAnswers={false} regenKeys={['rule', 'plan', 'expert', 'privacy']} />
      </MasterCollapse>
      <div className="lbl" style={{ marginTop: 14 }}>제출 서류 (7) · 마스터값으로 자동 생성, 수정·날인본 업로드하면 교체</div>
      <S3Row no={1} title="VICS ID · PW" note="벤처투자종합포털 로그인 정보 (제출·등록용)">
        <div className="s3in">
          <input className="pp-in" value={s3.vics.id} onChange={(e) => setVics('id', e.target.value)} placeholder="VICS 아이디" />
          <input className="pp-in" type="text" value={s3.vics.pw} onChange={(e) => setVics('pw', e.target.value)} placeholder="VICS 비밀번호" />
        </div>
        <div className="s3file" style={{ marginTop: 7 }}>
          <a className="upbtn link" href="https://install.kvic.or.kr/sda/sda/" target="_blank" rel="noopener noreferrer">🔗 VICS 회원가입·로그인 바로가기</a>
          <span className="s3muted" style={{ marginLeft: 2 }}>install.kvic.or.kr · 별도 탭</span>
          <span className="grow" />
          <span className="s2tag" title="입력 즉시 자동 저장되며 7단계에도 같은 값이 쓰입니다">자동 저장 · 7단계 공용</span>
        </div>
      </S3Row>
      <S3Row no={2} title="GP 교육 수료증" note="2단계 업로드 또는 1단계 제출분이 자동 연동됩니다">
        {s3.up.cert ? (
          <OkFile name={certDisp} pill="2단계 연동됨" onPv={certKey ? () => openKey(certKey, certDisp) : undefined} onDl={() => downloadUploadFile(p, certKey || '', certDisp, toast)} onRe={upCert} />
        ) : lkCert ? (
          <>
            <RefFile name={lkCert} tag="1단계 연동" onPv={() => onFilePv(lkCert, 'GP 교육 수료증')} onDl={() => downloadCollectFile(p, lkCert, toast)} />
            <div style={{ marginTop: 6 }}><UpBtn label="직접 업로드(교체)" small onUp={upCert} /></div>
          </>
        ) : (
          <UpBtn label="수료증 파일 업로드" onUp={upCert} />
        )}
      </S3Row>
      <S3Row no={3} title="개인투자조합 규약" note="자동 생성(Word) · 수정 후 업로드하면 교체">{gd('rule', false)}</S3Row>
      <S3Row no={4} title="조합 결성계획서" note="자동 생성(Word) · 수정 후 업로드하면 교체">{gd('plan', false)}</S3Row>
      <S3Row no={5} title="업무집행조합원 전문성 보유요건 충족 확인서" note="자동 생성(Word) · 날인 후 업로드하면 교체">{gd('expert', true)}</S3Row>
      <S3Row no={6} title="개인(신용)정보 수집·이용·제공 동의서 (GP용)" note="자동 생성(Word) · 날인 후 업로드하면 교체">{gd('privacy', true)}</S3Row>
      <S3Row no={7} title="조합 주소지 등기부등본" note="인터넷등기소에서 발급 후 업로드">
        <div className="s3addr">발급 대상 주소 · <b>{m && m.addr ? m.addr : '(마스터에서 조합 소재지를 입력하세요)'}</b></div>
        {s3.up.reg
          ? <OkFile name={regDisp} onPv={regKey ? () => openKey(regKey, regDisp) : undefined} onDl={() => downloadUploadFile(p, regKey || '', regDisp, toast)} onRe={upReg} />
          : (
            <div className="s3file">
              <a className="upbtn link" href="https://www.iros.go.kr" target="_blank" rel="noopener noreferrer">🔗 인터넷등기소 바로가기</a>
              <span className="grow" />
              <UpBtn label="발급본 업로드" small arrow={false} onUp={upReg} />
            </div>
          )}
      </S3Row>
    </div>
  );
}
